import { useMemo } from 'react';
import {
  ConnectNowAvailabilityStatus,
  ConnectNowAvailabilityStatuses,
  ConnectNowContactMethod,
  ConnectNowContactMethods,
  ContactMethodDefinition,
} from '../connectNowTypes';

const SERVICE_HOURS_ORDER: ConnectNowContactMethod[] = [
  ConnectNowContactMethods.Video,
  ConnectNowContactMethods.WebChat,
  ConnectNowContactMethods.SMS,
  ConnectNowContactMethods.Phone,
];

const AFTER_HOURS_ORDER: ConnectNowContactMethod[] = [
  ConnectNowContactMethods.Phone,
  ConnectNowContactMethods.Video,
  ConnectNowContactMethods.WebChat,
  ConnectNowContactMethods.SMS,
];

export type ContactMethodWithAvailability = ContactMethodDefinition & {
  availabilityStatus: ConnectNowAvailabilityStatus;
};

type UseConnectNowContactMethodsProps = {
  methods: ContactMethodDefinition[];
  isDuringServiceHours: boolean;
  isDuringHoliday: boolean;
};

/**
 *
 * Returns a sorted list of contact methods with availability status
 */
export function useConnectNowContactMethods({
  methods,
  isDuringServiceHours,
  isDuringHoliday,
}: UseConnectNowContactMethodsProps): ContactMethodWithAvailability[] {
  return useMemo(() => {
    return methods
      .map(method => {
        // Determine availability status
        let availabilityStatus: ConnectNowAvailabilityStatus;
        if (method.is247 || (isDuringServiceHours && !isDuringHoliday)) {
          availabilityStatus = ConnectNowAvailabilityStatuses.Available;
        } else if (isDuringHoliday) {
          availabilityStatus = ConnectNowAvailabilityStatuses.Holiday;
        } else {
          availabilityStatus = ConnectNowAvailabilityStatuses.AfterHours;
        }

        return {
          ...method,
          availabilityStatus,
        };
      })
      .sort((activeContactMethodA, activeContactMethodB) => {
        if (isDuringServiceHours && !isDuringHoliday) {
          return (
            SERVICE_HOURS_ORDER.indexOf(activeContactMethodA.type) -
            SERVICE_HOURS_ORDER.indexOf(activeContactMethodB.type)
          );
        }

        const aIsAvailable =
          activeContactMethodA.availabilityStatus === ConnectNowAvailabilityStatuses.Available;
        const bIsAvailable =
          activeContactMethodB.availabilityStatus === ConnectNowAvailabilityStatuses.Available;

        if (aIsAvailable !== bIsAvailable) {
          return aIsAvailable ? -1 : 1;
        }

        return (
          AFTER_HOURS_ORDER.indexOf(activeContactMethodA.type) -
          AFTER_HOURS_ORDER.indexOf(activeContactMethodB.type)
        );
      });
  }, [methods, isDuringServiceHours, isDuringHoliday]);
}
